// When a prop account hits its max loss, Home shows what happened (blownAccount.js): the last
// days, the trade that crossed the line, the likely cause with its proof, one rule to add to
// Morning prep, a replay of that day in the practice account, and wrapping the account up with
// the answers already filled in. Calm, not scolding: the point is what to take from it.
// Admin only for now (gold).
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { ArchiveAccountDialog } from './AccountEnded';
import { isProp } from './business';
import { blownReport } from './blownAccount';
import { useI18n } from './i18n';
import { currentLocale } from './langState';
import { formatMoney } from './pnl';
import { rootSymbol } from './tools/contracts';
import { drillFor } from './course/drills';

const dismissKey = (id, day) => `mm-blown-${id}-${day}`;
const isDismissed = (id, day) => { try { return localStorage.getItem(dismissKey(id, day)) === '1'; } catch { return false; } };
const longDate = (day) => new Date(`${day}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric' });
const timeOf = (iso) => new Date(iso).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' });

function Card({ account, report, trades, onAccountChange, onSessionExpired, onDismiss }) {
  const { t } = useI18n();
  const [added, setAdded] = useState('');
  const [ending, setEnding] = useState(false);
  const { top, crossing } = report;
  const second = report.causes[1];

  async function addRule() {
    try {
      const routine = await api('/routine');
      const rules = routine.rules || [];
      if (!rules.includes(report.rule)) await api('/routine', { method: 'PUT', body: { rules: [...rules, report.rule] } });
      setAdded('ok');
    } catch (err) {
      if (err.status === 401 && onSessionExpired) onSessionExpired();
      else setAdded(err.message);
    }
  }

  const cause = (c) => (c.kind === 'bleed'
    ? t('{n} of the last {days} trading days were losing days, {cost} in all.', { n: c.losingDays, days: report.windowDays, cost: formatMoney(c.cost) })
    : c.kind === 'bigDay'
      ? t('The last day alone lost {cost}.', { cost: formatMoney(c.cost) })
      : t(c.trades === 1 ? '1 trade, {cost}.' : '{n} trades, {cost} in all.', { n: c.trades, cost: formatMoney(c.cost) }));

  return (
    <section className="home-section blown-card admin-preview-box" aria-label={t('What happened to {name}', { name: account.name })}>
      <div className="blown-head">
        <h2>💥 {t('{name} hit its max loss', { name: account.name })} <span className="admin-preview-badge">{t('Admin only')}</span></h2>
        <button type="button" className="link-button" onClick={onDismiss}>{t('Not now')}</button>
      </div>
      <p className="blown-intro">{t('On {date}. It happens to almost every funded trader at some point. What matters is what you take from it.', { date: longDate(report.blownDay) })}</p>

      <div className="blown-grid">
        <div>
          <h3>{t('What happened')}</h3>
          <ul className="blown-days">
            {report.lastDays.map((d) => (
              <li key={d.day}><span>{longDate(d.day)}</span><span>{t(d.trades === 1 ? '1 trade' : '{n} trades', { n: d.trades })}</span><b className={d.net >= 0 ? 'gain' : 'loss'}>{formatMoney(d.net)}</b></li>
            ))}
          </ul>
          {crossing && (
            <p className="field-hint">{t('The trade that crossed the line: {time}, {side} {size} {symbol}, {net}.', { time: timeOf(crossing.date), side: t(crossing.side), size: crossing.size, symbol: crossing.symbol, net: formatMoney(crossing.pnl - (crossing.fees || 0)) })}</p>
          )}
        </div>
        <div>
          <h3>{t('Likely cause')}</h3>
          {top ? (
            <>
              <p className="blown-cause"><strong>{top.title}</strong> · {cause(top)}</p>
              {second && <p className="field-hint">{t('Also: {title}', { title: second.title })} · {cause(second)}</p>}
            </>
          ) : <p className="field-hint">{t('No single mistake stands out in the trades. Add a note about what happened when you wrap it up.')}</p>}
          <h3>{t('One rule to take from it')}</h3>
          <p className="blown-rule">“{report.rule}”</p>
          {added === 'ok' ? <p className="field-hint">✓ {t('Added to your rules in Morning prep.')}</p> : (
            <button type="button" className="small" onClick={addRule}>{t('Add to my rules')}</button>
          )}
          {added && added !== 'ok' && <p className="error">{added}</p>}
        </div>
      </div>

      <div className="blown-actions">
        <Link to={`/academy/chart-lab?day=${report.blownDay}&symbol=${rootSymbol(report.symbol)}`} className="bt-link-btn">{t('Replay that day in Chart Lab')}</Link>
        {top && drillFor(top.kind) && <Link to={`/academy/chart-lab?drill=${drillFor(top.kind)}`} className="bt-link-btn">{t('Drill the fix')}</Link>}
        <button type="button" className="secondary small" onClick={() => setEnding(true)}>{t('Wrap up this account')}</button>
      </div>

      {ending && (
        <ArchiveAccountDialog account={account} trades={trades} defaults={{ cause: report.endCause, behavior: report.top ? report.top.kind : '', lesson: report.rule, end: report.blownDay }}
          onClose={() => setEnding(false)} onSessionExpired={onSessionExpired}
          onDone={(updated) => { setEnding(false); if (onAccountChange) onAccountChange(updated); }} />
      )}
    </section>
  );
}

export default function BlownCard({ user, accounts, trades, notes, onAccountChange, onSessionExpired }) {
  const [, setTick] = useState(0);
  const reports = useMemo(() => {
    if (!user || !user.isAdmin || user.isDemo) return [];
    const prop = accounts.filter((a) => !a.archived && !a.attemptOf && isProp(a) && a.maxLossLimit);
    return prop.map((a) => {
      const mine = trades.filter((x) => String(x.accountId) === String(a._id));
      return { account: a, trades: mine, report: blownReport(a, mine, { allTrades: trades, accounts, notes }) };
    }).filter((r) => r.report);
  }, [user, accounts, trades, notes]);
  const shown = reports.filter((r) => !isDismissed(r.account._id, r.report.blownDay));
  if (!shown.length) return null;
  return shown.map((r) => (
    <Card key={r.account._id} {...r} onAccountChange={onAccountChange} onSessionExpired={onSessionExpired}
      onDismiss={() => { try { localStorage.setItem(dismissKey(r.account._id, r.report.blownDay), '1'); } catch { /* shown again next visit */ } setTick((n) => n + 1); }} />
  ));
}
